// Extracted from legacy /app/frontend/src/App.js DashboardPage (lines 409-762).
// Mock data preserved as-is; visuals untouched. Imports and `setActivePage`
// adapted to work as a stand-alone component used by UserPanel.
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Clock, Calendar, Plus, Package, CheckCircle, XCircle,
  Building2, MapPin, Mail, Phone, MessageSquare, FileText, ArrowRight, Wallet,
  TrendingUp, TrendingDown, DollarSign, Activity, BarChart3, AlertCircle, Zap, Award,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend as RcLegend,
  PieChart, Pie, Cell,
} from 'recharts';
import OrderDetailDrawer from './OrderDetailDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const fmtMoney = (n) => {
  try { return new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(n) || 0); }
  catch (_) { return `€${(Number(n) || 0).toFixed(2)}`; }
};
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1)   return `${Math.round(h * 60)}m`;
  if (h < 48)  return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};
const PIE_COLORS = ['#10b981', '#06b6d4', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];
// Map order statuses to display labels + status-aware colors so the donut and
// the legend stay readable across all customer datasets.
const STATUS_META = {
  completed: { label: 'Completed', color: '#10b981' },
  open:      { label: 'Open',      color: '#06b6d4' },
  working:   { label: 'Working',   color: '#f59e0b' },
  waiting:   { label: 'Waiting',   color: '#8b5cf6' },
  pending:   { label: 'Pending',   color: '#64748b' },
  cancelled: { label: 'Cancelled', color: '#ef4444' },
  closed:    { label: 'Closed',    color: '#475569' },
};
const statusMeta = (key) => STATUS_META[key] || { label: (key || '—').replace(/\b\w/g, (c) => c.toUpperCase()), color: '#64748b' };

export default function UserDashboard({ token, setActivePage, onOpenOrders }) {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { t: tOrders } = useTranslation('orders');
  const localeCode = i18n.language || 'en';
  const tNav = (k) => tCommon(`nav.${k}`);
  const [statsFilter, setStatsFilter] = useState('30d');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [whConfig, setWhConfig] = useState(null);
  // Live order stats fetched from the backend so the counter cards show the
  // *actual* number of orders for this user (no more mock 247/186/8/3).
  const [myStats, setMyStats] = useState({ total: 0, in_progress: 0, completed: 0, cancelled: 0 });
  const [recent, setRecent] = useState([]);        // live Recent Orders
  const [summary, setSummary] = useState(null);    // /my-summary payload (money + breakdowns + buckets)
  // Open a specific order's details drawer from the Recent Orders card
  // without leaving the dashboard. Stores just the id; drawer fetches the
  // full record from the API.
  const [openOrderId, setOpenOrderId] = useState(null);
  // Resolved seller company (same routing as PayPal + admin's "Current
  // routing"). Display-only — never expose paypal credentials here.
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Pull the user's own order stats once and refresh every 30s.
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/my-stats`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok || stop) return;
        setMyStats(await r.json());
      } catch (_) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [token]);

  // Recent orders (last 5) — for the "Recent Orders" card. Polls every 30s.
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/?limit=5`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok || stop) return;
        setRecent(await r.json());
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [token]);

  // Personal summary (money + breakdowns + buckets) — refetches on filter change.
  useEffect(() => {
    if (!token) return;
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/my-summary?range=${statsFilter}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok || stop) return;
        setSummary(await r.json());
      } catch (_) {}
    };
    load();
    return () => { stop = true; };
  }, [token, statsFilter]);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/working-hours`);
        if (!r.ok || stop) return;
        setWhConfig(await r.json());
      } catch (_) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const tzOffset = Number(whConfig?.timezone_offset ?? 3);
  const tzLabel = `GMT${tzOffset >= 0 ? '+' : ''}${tzOffset}`;

  // Fetch the seller company assigned to this customer by the same
  // routing logic as PayPal + admin "Current routing". One-shot —
  // company assignment is essentially static for a given customer;
  // a page refresh re-resolves after admin changes routing rules.
  useEffect(() => {
    if (!token) return;
    let stop = false;
    (async () => {
      try {
        setCompanyLoading(true);
        const r = await fetch(`${API_URL}/api/payments/my-company-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || stop) return;
        const j = await r.json();
        if (!stop) setCompanyInfo(j?.company_information || null);
      } catch (_) {
        /* silent — dashboard stays usable without the company card */
      } finally {
        if (!stop) setCompanyLoading(false);
      }
    })();
    return () => { stop = true; };
  }, [token]);

  // Compute "now" in the configured TZ (independent of the viewer's local TZ).
  // Use UTC accessors on the shifted date to read shifted hour/minute/weekday.
  const utcMs = currentTime.getTime() + currentTime.getTimezoneOffset() * 60000;
  const tzNow = new Date(utcMs + tzOffset * 3600000);

  const pad = (n) => String(n).padStart(2, '0');
  const formatDate = (d) => d.toLocaleDateString(localeCode, { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });

  const tzNowDisplay = `${pad(tzNow.getUTCHours())}:${pad(tzNow.getUTCMinutes())}`;
  const tzWeekdayMon0 = (tzNow.getUTCDay() + 6) % 7; // Mon=0..Sun=6

  // Build working hours rows in MON..SUN order from API config; fall back to defaults
  const apiDays = (whConfig?.days || []);
  const workingHours = DAY_KEYS.map((dkey, i) => {
    const label = t(`days.${dkey}`, { defaultValue: dkey.toUpperCase() });
    const d = apiDays.find((x) => Number(x.day) === i);
    if (!d) return { day: label, hours: '07:00 - 23:00', closed: false };
    return {
      day: label,
      hours: d.closed ? t('labels.closed_now') : `${d.open || '07:00'} - ${d.close || '23:00'}`,
      closed: !!d.closed,
    };
  });

  return (
    <div className="space-y-6 max-w-[88rem] mx-auto p-4 md:p-6" data-testid="user-dashboard">
      {/* Top Section: compact unified bar — Time · Date · Quick Actions.
          Previously three fat cards (p-5 each with 3xl/2xl values) that ate
          most of the fold. Now a single row with trimmed typography; on
          mobile the two info pills sit above the action buttons. */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        {/* Time pill */}
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-sm">
          <Clock className="w-4 h-4 opacity-90" />
          <div className="leading-tight">
            <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{t('labels.current_time_lbl')}</div>
            <div className="text-lg font-bold font-mono tabular-nums">{tzNowDisplay}</div>
          </div>
          <span className="ml-auto md:ml-2 text-[10px] opacity-90 hidden sm:inline">{tzLabel}</span>
        </div>
        {/* Date pill */}
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
          <Calendar className="w-4 h-4 text-slate-500" />
          <div className="leading-tight">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{t('labels.today')}</div>
            <div className="text-sm font-bold text-slate-800">
              {formatDate(tzNow)}
              <span className="text-slate-500 font-medium ml-1.5">
                · {tzNow.toLocaleDateString(localeCode, { weekday: 'short', timeZone: 'UTC' })}
              </span>
            </div>
          </div>
        </div>
        {/* Quick actions — right-aligned buttons, same function, compact */}
        <div className="flex items-center gap-2 md:ml-auto">
          <button
            onClick={() => setActivePage && setActivePage('new-order')}
            data-testid="dashboard-new-order-btn"
            className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md active:scale-[0.99] transition-all font-semibold text-xs sm:text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>{tNav('new_order')}</span>
            <span className="hidden md:inline text-[10px] font-normal opacity-80">· {t('actions.get_price')}</span>
            <ArrowRight className="w-3.5 h-3.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            onClick={() => setActivePage && setActivePage('recharge')}
            data-testid="dashboard-recharge-btn"
            className="group relative flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md hover:shadow-lg hover:from-emerald-600 hover:to-teal-700 active:scale-[0.99] transition-all font-semibold text-xs sm:text-sm overflow-hidden"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out" aria-hidden="true" />
            <Wallet className="w-4 h-4" />
            <span>{tNav('recharge')}</span>
            <ArrowRight className="w-3.5 h-3.5 opacity-80 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>

      {/* Stats Cards — compact (half the height of the previous layout). */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ClickableStatCard
          icon={Package}     iconBg="bg-cyan-100"  iconColor="text-cyan-600"
          badge={t('badges.total')}       value={myStats.total}        label={t('stats.total_orders')}
          testid="stat-card-total"
          onClick={() => onOpenOrders && onOpenOrders('')}
        />
        <ClickableStatCard
          icon={CheckCircle} iconBg="bg-green-100" iconColor="text-green-600"
          badge={t('badges.all_time')}    value={myStats.completed}    label={t('stats.completed_orders')}
          testid="stat-card-completed"
          onClick={() => onOpenOrders && onOpenOrders('completed')}
        />
        <ClickableStatCard
          icon={Clock}       iconBg="bg-amber-100" iconColor="text-amber-600"
          badge={t('badges.active')}      value={myStats.in_progress}  label={t('stats.working_orders')}
          testid="stat-card-in-progress"
          // No single "in_progress" status server-side — open is the most common.
          // We use "open" filter as a reasonable approximation; user can clear it in the panel.
          onClick={() => onOpenOrders && onOpenOrders('open')}
        />
        <ClickableStatCard
          icon={XCircle}     iconBg="bg-red-100"   iconColor="text-red-600"
          badge={t('badges.all_time')}    value={myStats.cancelled}    label={t('stats.cancelled_orders')}
          testid="stat-card-cancelled"
          onClick={() => onOpenOrders && onOpenOrders('cancelled')}
        />
      </div>

      {/* Opening Times */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-cyan-600" />
          <h3 className="text-lg font-bold text-slate-800">{t('widgets.opening_times')} {tzLabel}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {workingHours.map((item, idx) => {
            const isToday = tzWeekdayMon0 === idx;
            const isWeekend = idx >= 5;
            return (
              <div key={item.day}
                className={`p-4 rounded-lg border-2 text-center transition-all ${
                  item.closed
                    ? 'border-rose-200 bg-rose-50'
                    : isToday
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-slate-200 bg-slate-50'
                }`}
                data-testid={`dashboard-day-${idx}`}>
                <div className={`text-xs font-bold mb-2 ${
                  item.closed
                    ? 'text-rose-700'
                    : isToday
                      ? 'text-cyan-700'
                      : isWeekend
                        ? 'text-slate-500'
                        : 'text-slate-700'
                }`}>{item.day}</div>
                <div className={`text-sm ${
                  item.closed
                    ? 'text-rose-600 font-semibold'
                    : isToday
                      ? 'text-cyan-600 font-semibold'
                      : 'text-slate-600'
                }`}>{item.hours}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ───── My statistics ─────────────────────────────────────────
          Period-scoped personal analytics: money summary, orders/spend chart,
          orders by status, top services, top vehicles, operational stats.
          Everything below is hard-scoped to the logged-in user. */}
      <div className="bg-white rounded-xl p-4 md:p-5 border border-slate-200 shadow-sm" data-testid="my-statistics-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <BarChart3 className="w-5 h-5 text-cyan-600 flex-shrink-0" />
            <h2 className="text-lg font-bold text-slate-800">{t('widgets.my_statistics')}</h2>
            <span className="text-xs text-slate-400 hidden sm:inline">· {t('labels.your_activity_only')}</span>
          </div>
          {/* Compact period selector — horizontal scroll on tight viewports
              so 5 pills never wrap the way buttons do. */}
          <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 sm:overflow-visible">
            {[
              { id: '7d',         label: t('periods.d7') },
              { id: '30d',        label: t('periods.d30') },
              { id: 'this_month', label: t('periods.this_month') },
              { id: 'this_year',  label: t('periods.this_year') },
              { id: 'all',        label: t('periods.all_time') },
            ].map((p) => (
              <button
                key={p.id}
                data-testid={`dash-range-${p.id}`}
                onClick={() => setStatsFilter(p.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                  statsFilter === p.id
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {/* Money summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-4" data-testid="my-money-cards">
          <MoneyCard testid="my-spent"            icon={DollarSign}   label={t('money.spent')}           value={fmtMoney(summary?.money?.spent || 0)}            sub={t('money.spent_sub_many', { count: summary?.counts?.completed || 0 })}              tone="emerald" tooltip={t('money.spent_tooltip')} />
          <MoneyCard testid="my-orders-amount"    icon={Package}      label={t('money.orders_amount')}   value={fmtMoney(summary?.money?.orders_amount || 0)}    sub={t('money.orders_amount_sub_many', { count: summary?.counts?.total || 0 })}                     tone="cyan"    tooltip={t('money.orders_amount_tooltip')} />
          <MoneyCard testid="my-aov"              icon={Activity}     label={t('money.average_order')}   value={fmtMoney(summary?.money?.avg_order || 0)}        sub={t('money.average_order_sub')}                                                    tone="violet"  tooltip={t('money.average_order_tooltip')} />
          <MoneyCard testid="my-recharged"        icon={Wallet}       label={t('money.recharged')}       value={fmtMoney(summary?.money?.recharged || 0)}        sub={t('money.recharged_sub_many', { count: summary?.money?.recharged_count || 0 })}            tone="amber"   tooltip={t('money.recharged_tooltip')} />
          <MoneyCard testid="my-balance-movement" icon={(summary?.money?.balance_movement ?? 0) >= 0 ? TrendingUp : TrendingDown}
                                                  label={t('money.balance_movement')}
                                                  value={`${(summary?.money?.balance_movement ?? 0) >= 0 ? '+' : ''}${fmtMoney(summary?.money?.balance_movement || 0)}`}
                                                  sub={t('money.balance_movement_sub')}
                                                  tone={(summary?.money?.balance_movement ?? 0) >= 0 ? 'cyan' : 'rose'}
                                                  tooltip={t('money.balance_movement_tooltip')} />
        </div>

        {/* Orders & spend chart — bars (counts) + line (spent €). */}
        <SpendChart summary={summary} />
      </div>

      {/* Operational stats strip — speed + active + needs-attention. */}
      <OperationalStats summary={summary} setActivePage={setActivePage} />

      {/* Two-column: Orders by status + Top services / Top vehicles. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <StatusBreakdown summary={summary} />
        <div className="space-y-4 md:space-y-5">
          <TopServices summary={summary} />
          <TopVehicles summary={summary} />
        </div>
      </div>

      {/* Two Column: Company / Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="dashboard-company-card">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-cyan-600" />
            <h3 className="text-lg font-bold text-slate-800">{t('widgets.company_information')}</h3>
          </div>
          {companyLoading && !companyInfo ? (
            <div className="space-y-3 animate-pulse" data-testid="dashboard-company-skeleton">
              <div className="h-4 bg-slate-100 rounded w-1/2" />
              <div className="h-3 bg-slate-100 rounded w-2/3" />
              <div className="h-3 bg-slate-100 rounded w-3/4" />
              <div className="h-3 bg-slate-100 rounded w-1/2" />
            </div>
          ) : !companyInfo ? (
            <div className="text-sm text-slate-500" data-testid="dashboard-company-empty">
              {t('company.setup_message')}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="pb-3 border-b border-slate-200">
                <div className="text-xs text-slate-500 mb-1">{t('fields.company_name')}</div>
                <div
                  className="text-sm font-bold text-slate-800"
                  data-testid="dashboard-company-name"
                >
                  {companyInfo.company_name || '—'}
                </div>
                {(companyInfo.vat || companyInfo.company_number) && (
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                    {companyInfo.company_number && (
                      <span data-testid="dashboard-company-number">
                        {t('company.reg_number')} {companyInfo.company_number}
                      </span>
                    )}
                    {companyInfo.vat && (
                      <span data-testid="dashboard-company-vat">
                        {t('company.vat')} {companyInfo.vat}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {(companyInfo.address || companyInfo.city || companyInfo.country) && (
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-500 mb-1">{t('company.address')}</div>
                    {companyInfo.address && (
                      <div
                        className="text-sm text-slate-700"
                        data-testid="dashboard-company-address"
                      >
                        {companyInfo.address}
                      </div>
                    )}
                    {(companyInfo.city || companyInfo.country) && (
                      <div
                        className="text-sm text-slate-700"
                        data-testid="dashboard-company-locale"
                      >
                        {[companyInfo.city, companyInfo.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2 pt-2 border-t border-slate-200">
                {companyInfo.email && (
                  <ContactRow
                    icon={Mail}
                    color="text-cyan-600"
                    label={t('company.main_email')}
                    value={companyInfo.email}
                    testid="dashboard-company-email"
                  />
                )}
                {companyInfo.support_email && companyInfo.support_email !== companyInfo.email && (
                  <ContactRow
                    icon={Mail}
                    color="text-blue-600"
                    label={t('company.support_email')}
                    value={companyInfo.support_email}
                    testid="dashboard-company-support-email"
                  />
                )}
                {companyInfo.billing_email && companyInfo.billing_email !== companyInfo.email && (
                  <ContactRow
                    icon={Mail}
                    color="text-green-600"
                    label={t('company.billing_email')}
                    value={companyInfo.billing_email}
                    testid="dashboard-company-billing-email"
                  />
                )}
                {companyInfo.phone && (
                  <ContactRow
                    icon={Phone}
                    color="text-emerald-600"
                    label={t('company.phone')}
                    value={companyInfo.phone}
                    testid="dashboard-company-phone"
                  />
                )}
              </div>

              <div className="pt-4 border-t border-slate-200">
                <div className="text-xs font-medium text-slate-600 mb-3">{t('company.contact_via_messengers')}</div>
                <div className="flex items-center gap-3">
                  <button className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 shadow-md"><Phone className="w-5 h-5 text-white" /></button>
                  <button className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 shadow-md"><MessageSquare className="w-5 h-5 text-white" /></button>
                  <button className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center hover:bg-sky-600 shadow-md" title="Telegram">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
                  </button>
                  <button className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center hover:bg-purple-600 shadow-md" title="WhatsApp">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z"/></svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 mb-4">{t('widgets.recent_orders')}</h3>
          <div className="space-y-3">
            {recent.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">{t('empty_states.no_recent_orders')}</p>
            ) : recent.slice(0, 5).map((o) => {
              const fmtDate = (iso) => {
                if (!iso) return '';
                const d = new Date(iso);
                if (Number.isNaN(d.getTime())) return '';
                const pad = (n) => String(n).padStart(2, '0');
                return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
              };
              const statusCls = {
                completed: 'bg-green-100 text-green-700',
                working:   'bg-amber-100 text-amber-700',
                cancelled: 'bg-red-100 text-red-700',
                open:      'bg-blue-100 text-blue-700',
                waiting:   'bg-slate-100 text-slate-700',
              }[o.status] || 'bg-slate-100 text-slate-700';
              const statusLabel = tOrders(`status.${o.status}`, { defaultValue: ((o.status || '').charAt(0).toUpperCase() + (o.status || '').slice(1)) });
              return (
                <button key={o.id}
                  onClick={() => setOpenOrderId(o.id)}
                  data-testid={`recent-order-${o.id}`}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 hover:shadow-sm transition-all text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-cyan-700 underline">#{o.order_number || o.id.slice(0, 8)}</div>
                      <div className="text-xs text-slate-500">{o.manufacturer || '—'} {o.model || ''}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-medium px-2 py-1 rounded ${statusCls}`}>{statusLabel}</div>
                    <div className="text-xs text-slate-500 mt-1">{fmtDate(o.created_at)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Order detail drawer — opened from Recent Orders row click */}
      {openOrderId && (
        <OrderDetailDrawer
          order={{ id: openOrderId }}
          token={token}
          isAdmin={false}
          onClose={() => setOpenOrderId(null)}
          onChanged={() => { /* Dashboard data is polled — drawer actions will reflect on next tick */ }}
        />
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, iconBg, iconColor, badge, value, label }) => (
  <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-2">
      <div className={`w-8 h-8 ${iconBg} rounded-md flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <span className="text-[10px] text-slate-500 uppercase tracking-wider">{badge}</span>
    </div>
    <div className="text-xl font-bold text-slate-800 leading-tight">{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

// Same look as StatCard but rendered as a real <button> so screen readers and
// keyboard users can fire the navigation. Hover lift = visual hint of clickability.
const ClickableStatCard = ({ icon: Icon, iconBg, iconColor, badge, value, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="group text-left bg-white rounded-lg p-3 border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500"
  >
    <div className="flex items-center justify-between mb-2">
      <div className={`w-8 h-8 ${iconBg} rounded-md flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <span className="text-[10px] text-slate-500 uppercase tracking-wider">{badge}</span>
    </div>
    <div className="text-xl font-bold text-slate-800 leading-tight">{Number(value || 0).toLocaleString()}</div>
    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
      {label}
      <span className="text-cyan-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity">→</span>
    </div>
  </button>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-3 h-3 ${color} rounded-full`} />
    <span className="text-sm text-slate-600">{label}</span>
  </div>
);

const ContactRow = ({ icon: Icon, color, label, value, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <Icon className={`w-4 h-4 ${color}`} />
    <div className="flex-1">
      <span className="text-xs text-slate-500">{label}:</span>
      <span className={`text-sm ${color} ml-2 font-medium`}>{value}</span>
    </div>
  </div>
);

/* ─────────── Personal analytics helpers (My statistics section) ─────────── */

/**
 * Compact money/stat card used in the customer Money Summary row.
 * Title tooltip explains how the value is calculated so the customer
 * understands which slice of activity drives each number.
 */
function MoneyCard({ icon: Icon, label, value, sub, tone, tooltip, testid }) {
  const tones = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: '#10b981' },
    cyan:    { bg: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700',    dot: '#06b6d4' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700',  dot: '#8b5cf6' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   dot: '#f59e0b' },
    rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    dot: '#ef4444' },
  };
  const t = tones[tone] || tones.cyan;
  return (
    <div data-testid={testid} title={tooltip || ''}
         className={`relative px-3 py-2.5 rounded-lg ${t.bg} ${t.border} border ${tooltip ? 'cursor-help' : ''}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: t.dot }} />
          <span className={`text-[10px] uppercase font-bold tracking-wider truncate ${t.text} opacity-80`}>{label}</span>
        </div>
        <Icon className={`w-3.5 h-3.5 ${t.text} opacity-70 flex-shrink-0`} />
      </div>
      <div className={`text-lg font-extrabold tabular-nums leading-tight ${t.text}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

/** Orders & spend ComposedChart — bars=count by status, line=spent €. */
function SpendChart({ summary }) {
  const { t, i18n } = useTranslation('dashboard');
  const localeCode = i18n.language || 'en';
  const buckets = summary?.buckets || [];
  const hasData = buckets.some((b) => (b.count || 0) > 0 || (b.spent || 0) > 0);

  // Sparse multi-year data may produce one bar — keep label compact.
  const bucketSize = summary?.bucket_size;
  const friendlyLabel = (lbl) => {
    if (!lbl) return '';
    if (bucketSize === 'month') {
      // "2026-05" → "May 26"
      const [y, m] = lbl.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      return `${date.toLocaleString(localeCode, { month: 'short' })} ${String(y).slice(2)}`;
    }
    // day: "2026-05-22" → "22 May"
    const [y, m, d] = lbl.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return `${d} ${date.toLocaleString(localeCode, { month: 'short' })}`;
  };
  const data = buckets.map((b) => ({ ...b, label: friendlyLabel(b.label) }));

  if (!hasData) {
    return (
      <div className="border border-dashed border-slate-200 rounded-lg py-10 text-center" data-testid="my-chart-empty">
        <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 font-medium">{t('chart.not_enough_data')}</p>
        <p className="text-xs text-slate-400 mt-1">{t('chart.not_enough_data_hint')}</p>
      </div>
    );
  }

  return (
    <div data-testid="my-spend-chart">
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 2 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" stroke="#64748b" fontSize={11} interval="preserveStartEnd" />
          <YAxis yAxisId="count" stroke="#06b6d4" fontSize={11} allowDecimals={false} />
          <YAxis yAxisId="money" orientation="right" stroke="#10b981" fontSize={11}
                 tickFormatter={(v) => v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`} />
          <Tooltip content={<SpendTooltip />} />
          <RcLegend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          <Bar  yAxisId="count" dataKey="completed"   stackId="o" name={t('chart.completed')}   fill="#10b981" radius={[3, 3, 0, 0]} />
          <Bar  yAxisId="count" dataKey="in_progress" stackId="o" name={t('chart.in_progress')} fill="#f59e0b" />
          <Bar  yAxisId="count" dataKey="cancelled"   stackId="o" name={t('chart.cancelled')}   fill="#ef4444" />
          <Line yAxisId="money" type="monotone" dataKey="spent" name={t('chart.spent')} stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Custom tooltip — period + per-status counts + spent + AOV. */
function SpendTooltip({ active, payload, label }) {
  const { t } = useTranslation('dashboard');
  if (!active || !payload || !payload.length) return null;
  const b = payload[0].payload || {};
  const aov = b.completed ? (b.spent || 0) / b.completed : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px] min-w-[200px]" data-testid="my-spend-tooltip">
      <div className="font-bold text-slate-800 mb-1.5">{label}</div>
      <RowKV k={t('chart.completed')}   v={fmtNum(b.completed || 0)}   color="#10b981" />
      <RowKV k={t('chart.in_progress')} v={fmtNum(b.in_progress || 0)} color="#f59e0b" />
      <RowKV k={t('chart.cancelled')}   v={fmtNum(b.cancelled || 0)}   color="#ef4444" />
      <div className="mt-1.5 pt-1.5 border-t border-slate-100 space-y-0.5">
        <RowKV k={t('chart.spent_label')}       v={fmtMoney(b.spent || 0)}        bold color="#0f172a" />
        <RowKV k={t('chart.orders_amt')} v={fmtMoney(b.orders_amount || 0)} color="#64748b" />
        <RowKV k={t('chart.avg_order')}   v={fmtMoney(aov)}                   color="#64748b" />
      </div>
    </div>
  );
}

function RowKV({ k, v, color = '#64748b', bold = false }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-slate-600">{k}</span>
      </span>
      <span className={`font-mono tabular-nums ${bold ? 'font-bold text-slate-900' : 'text-slate-800'}`}>{v}</span>
    </div>
  );
}

/** Donut + legend for Orders by status, with count + amount per row. */
function StatusBreakdown({ summary }) {
  const { t } = useTranslation('dashboard');
  const { t: tOrders } = useTranslation('orders');
  const rows = summary?.by_status || [];
  const totalCount = rows.reduce((s, r) => s + (r.count || 0), 0);
  const statusLabel = (key) => {
    const meta = statusMeta(key);
    // Try orders namespace first for translated status labels
    const translated = tOrders(`status.${key}`, { defaultValue: '' });
    return translated || meta.label;
  };
  return (
    <div className="bg-white rounded-xl p-4 md:p-5 border border-slate-200 shadow-sm" data-testid="my-status-breakdown">
      <div className="flex items-center gap-2 mb-3">
        <Package className="w-4 h-4 text-cyan-600" />
        <h3 className="text-sm font-bold text-slate-800">{t('widgets.orders_by_status')}</h3>
        {totalCount > 0 && <span className="text-[11px] text-slate-400">· {fmtNum(totalCount)} {t('chart.orders').toLowerCase()}</span>}
      </div>
      {!rows.length ? (
        <p className="text-xs text-slate-400 text-center py-10">{t('empty_states.no_orders_period')}</p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <div className="w-full sm:w-[160px] h-[170px] flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="count" nameKey="key" innerRadius={42} outerRadius={68} paddingAngle={2} isAnimationActive={false}>
                  {rows.map((r, i) => (<Cell key={i} fill={statusMeta(r.key).color} />))}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  const pct = totalCount ? ((p.count || 0) / totalCount) * 100 : 0;
                  return (
                    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[11px]">
                      <div className="font-bold text-slate-800 mb-1">{statusLabel(p.key)}</div>
                      <RowKV k={t('chart.orders')}  v={fmtNum(p.count || 0)}    color={statusMeta(p.key).color} />
                      <RowKV k={t('chart.amount')}  v={fmtMoney(p.amount || 0)} color="#0f172a" bold />
                      <RowKV k={t('chart.share')}   v={`${pct.toFixed(1)}%`}    color="#64748b" />
                    </div>
                  );
                }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 w-full space-y-1.5 min-w-0">
            {rows.map((r) => {
              const m = statusMeta(r.key);
              const pct = totalCount ? ((r.count || 0) / totalCount) * 100 : 0;
              return (
                <div key={r.key} className="flex items-center justify-between text-xs" data-testid={`my-status-row-${r.key}`}>
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: m.color }} />
                    <span className="text-slate-700 truncate">{statusLabel(r.key)}</span>
                  </span>
                  <span className="font-mono tabular-nums text-slate-800 whitespace-nowrap ml-2">
                    {fmtNum(r.count)} · {fmtMoney(r.amount || 0)} <span className="text-slate-400">({pct.toFixed(0)}%)</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Service-friendly label — `dpf_fap_delete` → "DPF/FAP Delete" feel. */
function prettyService(name) {
  if (!name) return '—';
  // Already pretty (contains a space, slash, or capital letter)?
  if (/[A-Z]|[ /]/.test(name)) return name;
  return name.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function TopServices({ summary }) {
  const { t } = useTranslation('dashboard');
  const rows = summary?.top_services || [];
  return (
    <div className="bg-white rounded-xl p-4 md:p-5 border border-slate-200 shadow-sm" data-testid="my-top-services">
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-4 h-4 text-violet-600" />
        <h3 className="text-sm font-bold text-slate-800">{t('widgets.top_services')}</h3>
      </div>
      {!rows.length ? (
        <p className="text-xs text-slate-400 text-center py-6">{t('empty_states.no_top_services')}</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, idx) => (
            <div key={r.name} className="flex items-center justify-between text-xs gap-2" data-testid={`my-service-row-${idx}`}>
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-5 h-5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                <span className="text-slate-700 truncate">{prettyService(r.name)}</span>
              </span>
              <span className="font-mono tabular-nums text-slate-800 flex-shrink-0">
                {fmtNum(r.count)} <span className="text-slate-400">· {fmtMoney(r.amount || 0)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Top vehicles — tabs across Brands / Models / ECU. */
function TopVehicles({ summary }) {
  const { t } = useTranslation('dashboard');
  const [tab, setTab] = useState('brands');
  const data = {
    brands: summary?.top_brands || [],
    models: summary?.top_models || [],
    ecu:    summary?.top_ecu    || [],
  }[tab];
  return (
    <div className="bg-white rounded-xl p-4 md:p-5 border border-slate-200 shadow-sm" data-testid="my-top-vehicles">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-cyan-600" />
          <h3 className="text-sm font-bold text-slate-800">{t('widgets.most_used_vehicles')}</h3>
        </div>
        <div className="inline-flex bg-slate-100 rounded p-0.5 text-[10px] font-bold">
          {[
            { id: 'brands', label: t('vehicles_tabs.brands') },
            { id: 'models', label: t('vehicles_tabs.models') },
            { id: 'ecu',    label: t('vehicles_tabs.ecus') },
          ].map((tab2) => (
            <button key={tab2.id}
                    data-testid={`my-vehicles-tab-${tab2.id}`}
                    onClick={() => setTab(tab2.id)}
                    className={`px-2 py-0.5 rounded ${tab === tab2.id ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
            >{tab2.label}</button>
          ))}
        </div>
      </div>
      {!data.length ? (
        <p className="text-xs text-slate-400 text-center py-6">{t('empty_states.no_vehicles')}</p>
      ) : (
        <div className="space-y-1.5">
          {data.map((r, idx) => (
            <div key={r.key} className="flex items-center justify-between text-xs gap-2" data-testid={`my-vehicle-row-${tab}-${idx}`}>
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-5 h-5 rounded bg-cyan-100 text-cyan-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                <span className="text-slate-700 truncate">{r.key}</span>
              </span>
              <span className="font-mono tabular-nums text-slate-800 flex-shrink-0">
                {fmtNum(r.count)} <span className="text-slate-400">· {fmtMoney(r.amount || 0)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Operational stats strip — speed + active + needs-attention. */
function OperationalStats({ summary, setActivePage }) {
  const { t } = useTranslation('dashboard');
  const op = summary?.operational || {};
  return (
    <div className="bg-white rounded-xl p-4 md:p-5 border border-slate-200 shadow-sm" data-testid="my-operational">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-bold text-slate-800">{t('widgets.operational_snapshot')}</h3>
        <span className="text-[11px] text-slate-400 hidden sm:inline">· {t('operational.live_hint')}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <OpCard testid="op-active"  icon={Activity}    label={t('operational.active_orders')}
                value={fmtNum(op.active_orders || 0)}
                sub={op.active_orders ? t('operational.active_in_progress') : t('operational.active_caught_up')}
                tone="amber"
                onClick={op.active_orders ? () => setActivePage && setActivePage('orders') : null}
                tooltip={t('operational.active_tooltip')} />
        <OpCard testid="op-attention" icon={AlertCircle} label={t('operational.needs_attention')}
                value={fmtNum(op.needs_attention || 0)}
                sub={op.needs_attention ? t('operational.needs_attention_waiting') : t('operational.needs_attention_none')}
                tone={op.needs_attention ? 'rose' : 'slate'}
                onClick={op.needs_attention ? () => setActivePage && setActivePage('orders') : null}
                tooltip={t('operational.needs_attention_tooltip')} />
        <OpCard testid="op-avg-speed" icon={Clock}      label={t('operational.avg_completion')}
                value={fmtHours(op.avg_completion_hours)}
                sub={op.completed_in_window ? t('operational.avg_completion_sub_many', { count: op.completed_in_window }) : t('operational.avg_completion_none')}
                tone="cyan"
                tooltip={t('operational.avg_completion_tooltip')} />
        <OpCard testid="op-fastest" icon={Zap}        label={t('operational.fastest')}
                value={fmtHours(op.fastest_completion_hours)}
                sub={t('operational.fastest_sub')}
                tone="emerald"
                tooltip={t('operational.fastest_tooltip')} />
      </div>
    </div>
  );
}

function OpCard({ icon: Icon, label, value, sub, tone, tooltip, onClick, testid }) {
  const tones = {
    cyan:    { bg: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700' },
    slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700' },
  };
  const t = tones[tone] || tones.cyan;
  const clickable = typeof onClick === 'function';
  const Comp = clickable ? 'button' : 'div';
  return (
    <Comp
      data-testid={testid}
      title={tooltip || ''}
      onClick={clickable ? onClick : undefined}
      className={`text-left px-3 py-2.5 rounded-lg ${t.bg} ${t.border} border ${clickable ? 'hover:shadow-sm hover:-translate-y-0.5 transition-all cursor-pointer' : ''}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${t.text}`} />
        <span className={`text-[10px] uppercase font-bold tracking-wider ${t.text} opacity-80`}>{label}</span>
      </div>
      <div className={`text-lg font-extrabold tabular-nums leading-tight ${t.text}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </Comp>
  );
}
